<template>
  <div :id="giftCodeCampaignId" class="gift-code-campaign-detail-area">
    <HeadingThree>{{ title }}</HeadingThree>

    <div v-if="hasBanner" class="gift-code-campaign-detail-area__banner">
      <a
        v-if="bannerUrl"
        :href="bannerUrl"
        target="_blank"
        class="gift-code-campaign-detail-area__banner-link"
      >
        <img
          :src="bannerImageUrl"
          :alt="title"
          class="gift-code-campaign-detail-area__banner-image"
        />
      </a>
      <img
        v-else
        :src="bannerImageUrl"
        :alt="title"
        class="gift-code-campaign-detail-area__banner-image"
      />
    </div>

    <div
      v-if="hasExpirationDate"
      class="gift-code-campaign-detail-area__expiration"
    >
      ※利用期限：{{ formattedExpirationDate }}まで
    </div>

    <div class="gift-code-campaign-detail-area__description">
      <template v-if="hasStockCount">
        <span class="gift-code-campaign-detail-area__description-highlight">
          先着{{ stockCount }}名様
        </span>
        <span class="gift-code-campaign-detail-area__description-text">
          限定、
        </span>
      </template>
      <template v-if="minimumAvailablePrice">
        <span class="gift-code-campaign-detail-area__description-text-bold">
          {{ minimumAvailablePrice.toLocaleString() }}円以上
        </span>
        <span class="gift-code-campaign-detail-area__description-text">
          の購入時に
        </span>
      </template>
      <span class="gift-code-campaign-detail-area__description-highlight">
        {{ discountText }} OFF
      </span>
      <span class="gift-code-campaign-detail-area__description-text">
        でお贈りいただけます。下記のギフトコードをコピーし、決済時にご入力ください。
      </span>
    </div>

    <a
      class="gift-code-campaign-detail-area__attention-link"
      @click="openAttentionModal"
    >
      <AlertCircleOutline />
      <span class="gift-code-campaign-detail-area__attention-link-text">
        ギフトコード利用時の注意事項
      </span>
    </a>

    <div class="gift-code-campaign-detail-area__serial-container">
      <div class="gift-code-campaign-detail-area__serial-inner">
        <span class="gift-code-campaign-detail-area__serial-text">
          {{ serial }}
        </span>
        <ButtonSecondary
          class="gift-code-campaign-detail-area__copy-button"
          text="ギフトコードをコピー"
          @click-secondary-button="copySerial"
        />
      </div>
    </div>
  </div>
</template>

<script>
import HeadingThree from '../../../atoms/headlines/HeadingThree.vue'
import AlertCircleOutline from '../../../atoms/svg_icons/AlertCircleOutline.vue'
import ButtonSecondary from '../../../molecules/buttons/ButtonSecondary.vue'
import dayjs from 'dayjs'

export default {
  name: 'GiftCodeCampaignDetailArea',
  components: {
    HeadingThree,
    AlertCircleOutline,
    ButtonSecondary
  },
  props: {
    giftCodeCampaignId: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    bannerImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    bannerUrl: {
      type: String,
      required: false,
      default: ''
    },
    expirationDate: {
      type: String,
      required: false,
      default: ''
    },
    stockCount: {
      type: Number,
      required: false,
      default: 0
    },
    discountRate: {
      type: Number,
      required: false,
      default: 0
    },
    discountPrice: {
      type: Number,
      required: false,
      default: 0
    },
    serial: {
      type: String,
      required: true
    },
    attention: {
      type: String,
      required: true
    },
    minimumAvailablePrice: {
      type: Number,
      required: false,
      default: 0
    }
  },
  computed: {
    hasBanner() {
      return this.bannerImageUrl && this.bannerImageUrl.trim() !== ''
    },
    hasExpirationDate() {
      return this.expirationDate && dayjs(this.expirationDate).isValid()
    },
    formattedExpirationDate() {
      if (!this.hasExpirationDate) return ''
      return dayjs(this.expirationDate).format('YYYY年M月D日 HH:mm')
    },
    hasStockCount() {
      return this.stockCount > 0
    },
    discountText() {
      if (this.discountRate > 0) {
        const formattedRate =
          this.discountRate % 1 === 0
            ? Math.floor(this.discountRate)
            : this.discountRate
        return `${formattedRate}%`
      }
      if (this.discountPrice > 0) {
        return `${this.discountPrice.toLocaleString()}円`
      }
      return ''
    }
  },
  methods: {
    openAttentionModal() {
      this.$emit('open-attention-modal', {
        title: this.title,
        attention: this.attention
      })
    },
    async copySerial() {
      try {
        await navigator.clipboard.writeText(this.serial)
        this.$emit('copy-success', 'ギフトコードをコピーしました')
      } catch (error) {
        this.$emit('copy-error', 'ギフトコードのコピーに失敗しました')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.gift-code-campaign-detail-area {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__banner {
    &-image {
      object-fit: contain;
      width: 100%;
      height: auto;
    }

    &-link {
      display: block;
      text-decoration: none;
    }
  }

  &__expiration {
    @extend %body;
    color: $gray-1;
  }

  &__description {
    &-highlight {
      @extend %body-bold;
      color: $primary;
    }

    &-text-bold {
      @extend %body-bold;
    }

    &-text {
      @extend %body;
    }
  }

  &__attention-link {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: $link;

    &-text {
      @extend %body;
      color: $link;
      text-align: center;
      line-height: 1.6;
    }
  }

  &__serial-container {
    display: flex;
    padding: 8px;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    align-self: stretch;
    border-radius: 8px;
    background: $primary-lighten;
  }

  &__serial-inner {
    display: flex;
    padding: 8px 16px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 4px;
    background: $white;
    justify-content: space-between;
  }

  &__serial-text {
    @extend %body-bold;
    color: $black;
  }

  :deep(.gift-code-campaign-detail-area__copy-button) {
    width: auto;
    height: 36px;
    padding: 0 16px;
    font-size: 1.2rem;
  }
}
</style>
